import { t } from 'i18next'
import { z } from 'zod'

import { validadorEmailDuplicado } from '@/features/proposta/entities/Comprador/validator/email-duplicado-validator'
import { EstadoCivilCompradorPrincipalValidator } from '@/features/proposta/entities/Comprador/validator/estado-civil-comprador-principal-validator'
import { EstadoCivilVinculoValidator } from '@/features/proposta/entities/Comprador/validator/estado-civil-vinculo-validator'
import { tipoDoVinculoDoCompradorEnum } from '@/features/proposta/interfaces/proposta.enums'
import { contatoSchema, estadoCivilSchema, transformEstadoCivil } from '@/features/proposta/schemas/compradores/common'
import { tipoDeCliente } from '@/shared/interfaces/cliente.enums'
import {} from '@/shared/schemas/telefone.schema'
import { cpfValidator } from '@/shared/services/cpf-validator'
import { StringUtils } from '@/shared/utils'
import { nomeSobrenomeRegExp } from '@/shared/utils/regex'

export const compradorPessoaFisicaBase = z.object({
  id: z.string().optional(),
  cpf: z
    .string()
    .min(1, { message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_obrigatorio') })
    .refine(
      (cpf) => cpf && cpfValidator.isValid(cpf),
      t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_invalido'),
    ),
  email: z
    .string()
    .nullish()
    .refine(
      (email) => !email || email.length === 0 || z.string().email().safeParse(email).success,
      t('proposta:cadastro_compradores.steps.contatos.erro.email_invalido'),
    ),
  nome: z
    .string({ required_error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nome') })
    .max(100, { message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nome_maximo') })
    .trim()
    .regex(nomeSobrenomeRegExp, { message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nome') })
    .transform((v) => v.trim()),
  maiorDe18Anos: z
    .enum(['sim', 'nao', ''])
    .refine((v) => v !== '', { message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.maior_18') }),
  profissaoId: z.string().min(1, { message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.profissao') }),
  nacionalidadeId: z
    .string()
    .min(1, { message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nacionalidade') }),
  buscouCpf: z.boolean().default(false),
})

export const compradorVinculoSchema = z
  .object({
    emailsJaVinculados: z.record(z.string()),
    compradorVinculadoIds: z.array(z.string()).min(1, { message: 'É necessário vincular um procurador a um cliente.' }),
    tipo: z.enum([tipoDoVinculoDoCompradorEnum.Procurador, tipoDoVinculoDoCompradorEnum.Representante]),
    tipoDoCliente: z.nativeEnum(tipoDeCliente),
    imagem: z.string().nullish(),
    dados: compradorPessoaFisicaBase,
    contato: contatoSchema,
    estadoCivil: estadoCivilSchema
      .superRefine((data, ctx) => {
        const issue = new EstadoCivilVinculoValidator(data).validar()
        if (!issue) return
        ctx.addIssue({
          message: issue.message,
          path: issue.path,
          code: z.ZodIssueCode.custom,
        })
      })
      .transform(transformEstadoCivil),
  })
  .extend({ mode: z.enum(['create', 'edit']) })
  .superRefine(({ dados, contato, emailsJaVinculados }, ctx) => {
    validadorEmailDuplicado(
      { documento: dados.cpf, email: contato?.email, fieldPath: ['contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })

const compradorMaePaiSchema = z
  .object({
    emailsJaVinculados: z.record(z.string()),
    tipo: z.literal(tipoDoVinculoDoCompradorEnum.MaePai),
    imagem: z.string().nullish(),
    dados: compradorPessoaFisicaBase,
    contato: contatoSchema,
    estadoCivil: estadoCivilSchema
      .superRefine((data, ctx) => {
        const issue = new EstadoCivilVinculoValidator(data).validar()
        if (!issue) return
        ctx.addIssue({
          message: issue.message,
          path: issue.path,
          code: z.ZodIssueCode.custom,
        })
      })
      .transform(transformEstadoCivil),
  })
  .superRefine(({ dados, contato, emailsJaVinculados }, ctx) => {
    validadorEmailDuplicado(
      { documento: dados.cpf, email: contato?.email, fieldPath: ['contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })

export const compradorPessoaFisicaStepSchema = z
  .object({
    emailsJaVinculados: z.record(z.string()),
    imagem: z.string().nullish(),
    tipo: z.literal('PessoaFisica'),
    fluxoIndependente: z.enum(['MaePai', 'Conjuge']).nullish(),
    editouTipoAssinatura: z.boolean().default(false),
    tipoDoCliente: z.nativeEnum(tipoDeCliente),
    dados: compradorPessoaFisicaBase.superRefine((data, ctx) => {
      if (!data.maiorDe18Anos && !data.email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'PROPOSTA.SCREENS.CADASTRO_COMPRADOR_DADOS.INFORME_EMAIL',
          path: ['email'],
        })
      }
    }),
    contato: contatoSchema.nullish(),
    estadoCivil: estadoCivilSchema.transform(transformEstadoCivil).nullish(),
    maePai: compradorMaePaiSchema.nullish(),
    conjuge: z
      .object({
        dados: compradorPessoaFisicaBase,
        contato: contatoSchema,
        tipo: z.literal(tipoDoVinculoDoCompradorEnum.Conjuge).default(tipoDoVinculoDoCompradorEnum.Conjuge),
        imagem: z.string().nullish(),
      })
      .nullish(),
  })
  .extend({ mode: z.enum(['create', 'edit']) })
  .superRefine(({ dados, estadoCivil }, ctx) => {
    if (dados.maiorDe18Anos === 'sim') {
      const issue = new EstadoCivilCompradorPrincipalValidator(estadoCivil).run()
      if (!issue) return
      ctx.addIssue({
        message: issue.message,
        path: issue.path,
        code: z.ZodIssueCode.custom,
      })
    }
  })
  .superRefine(({ dados, contato, emailsJaVinculados }, ctx) => {
    if (dados.maiorDe18Anos === 'nao') return z.NEVER
    validadorEmailDuplicado(
      { documento: dados.cpf, email: contato?.email, fieldPath: ['contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })
  .superRefine(({ maePai, emailsJaVinculados }, ctx) => {
    validadorEmailDuplicado(
      { documento: maePai?.dados.cpf, email: maePai?.contato?.email, fieldPath: ['maePai', 'contato', 'email'] },
      emailsJaVinculados,
      ctx,
    )
  })
  .superRefine(({ dados, contato, conjuge, emailsJaVinculados }, ctx) => {
    if (!contato?.email) return z.NEVER
    const emailsJaVinculadosIncluindoConjuge = {
      ...emailsJaVinculados,
      [contato.email]: StringUtils.normalize(dados.cpf),
    }
    validadorEmailDuplicado(
      { documento: conjuge?.dados.cpf, email: conjuge?.contato?.email, fieldPath: ['conjuge', 'contato', 'email'] },
      emailsJaVinculadosIncluindoConjuge,
      ctx,
    )
  })

export type CompradorEstadoCivilSchemaOutput = z.output<typeof estadoCivilSchema>

export type CompradorVinculoStepSchemaInput = z.input<typeof compradorVinculoSchema>
export type CompradorVinculoStepSchemaOutput = z.output<typeof compradorVinculoSchema>

export type CompradorMaePaiStepSchemaInput = z.input<typeof compradorMaePaiSchema>

export type CompradorPessoaFisicaStepSchemaInput = z.input<typeof compradorPessoaFisicaStepSchema>
export type CompradorPessoaFisicaStepSchemaOutput = z.output<typeof compradorPessoaFisicaStepSchema>
